import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, momentLocalizer } from "react-big-calendar";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../Modal";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import moment from "moment";
import "moment/locale/sv.js";

import {
  addDays,
  dateFormatDate,
  twoDigits,
  zDate,
} from "../../helpers/dateFunctions";
import { daysLong } from "../../helpers/dateStructs";
import { Clock } from "flowbite-react-icons/outline";
import AccountIcon from "../Icons/AccountIcon";
import { CalEventInterface } from "../../interfaces/common";
import CustomButton from "../CustomButton";
// Using local modified version of 'react-big-calendar/lib/css/react-big-calendar.css'
import "./react-big-calendar.css";
// Using local modified version of 'react-big-calendar/lib/addons/dragAndDrop/styles.css'
import "./dragAndDropStyles.css";
import Api from "../../services/Api";
import { getLanguage } from "../../lang/helpers";

const DnDCalendar = withDragAndDrop(Calendar);

interface Props {
  startEvents: CalEventInterface[];
  save?: boolean;
  calType?: string;
  click?: Function;
  clickTime?: Function;
  calendarHeight?: number;
}

const CalendarBig: React.FC<Props> = ({
  startEvents,
  save,
  calType,
  click,
  clickTime,
  calendarHeight,
}) => {
  const api = Api.getInstance();
  const translation = useTranslation();
  const { t, i18n } = translation;
  const lang = getLanguage(i18n.language);
  const [selectedEvent, setSelectedEvent] = useState<CalEventInterface | null>(
    null
  );
  const [hourFrom, setHourFrom] = useState<number>(0);
  const [minuteFrom, setMinuteFrom] = useState<number>(0);
  const [hourTo, setHourTo] = useState<number>(0);
  const [minuteTo, setMinuteTo] = useState<number>(0);
  const [events, setEvents] = useState<CalEventInterface[]>(startEvents);
  const [messageId, setMessageId] = useState<number>(startEvents.length + 1);
  // From https://jquense.github.io/react-big-calendar/examples/index.html?path=/docs/addons-drag-and-drop-props--on-event-drop
  const localeLang = lang === "sv" ? "se" : "en-GB";
  moment.locale(localeLang, {
    week: {
      dow: 1,
      doy: 1,
    },
  });
  const localizer = momentLocalizer(moment);

  const messages = {
    previous: "<",
    next: ">",
    today: lang === "sv" ? "Idag" : "Today",
    month: lang === "sv" ? "Månad" : "Month",
    week: lang === "sv" ? "Vecka" : "Week",
    work_week: lang === "sv" ? "Vecka" : "Week",
    day: "D",
    agenda: "*",
    date: lang === "sv" ? "Datum" : "Date",
    time: "H",
  };

  const saveEvent = (remove: boolean, event: CalEventInterface) => {
    if (remove && event.dbId === 0) {
      return;
    }
    const data = {
      event: event,
      remove: remove,
    };
    setSelectedEvent(null);

    api.post("coach/availability/edit", data).then((response) => {
      if (remove) {
        console.log("Removed " + event.dbId);
      } else {
        const newId = response.json.id;
        if (newId !== event.dbId) {
          const newEvents = [...events];
          for (const newEvent of newEvents) {
            if (newEvent.id === event.id) {
              newEvent.dbId = newId;
            }
          }
          setEvents(newEvents);
        }
      }
      console.log("ok");
    });
  };

  const EmptyCustomToolbar = () => {
    return <></>;
  };

  const getTypeTitle = (title: string) => {
    if (title === "R") {
      return lang === "sv" ? "Varje vecka" : "Every week";
    } else if (title === "A") {
      return lang === "sv" ? "Tillgänglig" : "Available time";
    } else if (title === "B") {
      return lang === "sv" ? "Blockerad" : "Blocked";
    } else if (title === "S") {
      return lang === "sv" ? "Upptagen" : "Busy";
    } else if (title === "M") {
      return lang === "sv" ? "Möte" : "Meeting";
    }
  };

  const EventComponent = (e: CalEventInterface) => {
    const title = getTypeTitle(e.title);
    return <span className="font-medium">{title}</span>;
  };

  const eventStyleGetter = (e: CalEventInterface) => {
    // console.log(e)
    let bg = "#94ABFF";
    if (e.title === "B") {
      bg = "#FF6079";
    } else if (e.title === "S") {
      bg = "#EAF47F";
    } else if (e.title === "M") {
      bg = "#60B9A5";
    }
    const style = {
      backgroundColor: bg,
      color: "#000",
    };
    return {
      style: style,
    };
  };

  const getCalendarSettings = () => {
    const settings = {
      views: ["week", "month", "day"],
      defaultView: "week",
      components: {},
      selectable: true,
      resizeable: true,
    };
    if (calType === "recurring") {
      settings.views = ["work_week"];
      settings.defaultView = "work_week";
      settings.components = {
        work_week: {
          header: ({ date, localizer }) => localizer.format(date, "dddd"),
        },
        toolbar: EmptyCustomToolbar,
        event: EventComponent,
      };
    } else if (calType === "available") {
      settings.views = ["week"];
      settings.defaultView = "week";
      settings.components = {
        event: EventComponent,
      };
    } else if (calType === "summary") {
      settings.views = ["week", "month"];
      settings.defaultView = "week";
      settings.components = {
        event: EventComponent,
      };
      settings.selectable = false;
      settings.resizeable = false;
    } else if (calType === "book_meeting") {
      settings.views = ["work_week"];
      settings.defaultView = "work_week";
      settings.components = {
        event: EventComponent,
      };
      settings.selectable = false;
      settings.resizeable = false;
    } else if (calType === "book_meeting_free") {
      settings.views = ["week"];
      settings.defaultView = "week";
      settings.components = {
        event: EventComponent,
      };
      settings.selectable = true;
      settings.resizeable = false;
    }
    return settings;
  };

  const settings = getCalendarSettings();

  const addEvent = (e: any) => {
    if (save) {
      const newEvents = [...events];
      const newEvent = {
        id: messageId,
        dbId: 0,
        start: e.start,
        end: e.end,
        title: "A",
      };
      newEvents.push(newEvent);
      // console.log('addEvent for id ' + messageId)
      setMessageId(messageId + 1);
      setEvents(newEvents);
      // setSelectedEvent(newEvent)
      openEvent(newEvent);
    } else if (clickTime) {
      clickTime(e.start);
    } else {
      console.log("Not supposed to add stuff");
    }
  };

  const openEvent = (e: CalEventInterface) => {
    setHourFrom(e.start.getHours());
    setMinuteFrom(e.start.getMinutes());
    setHourTo(e.end.getHours());
    setMinuteTo(e.end.getMinutes());
    setSelectedEvent(e);
  };

  const editEvent = (e: CalEventInterface) => {
    if (click) {
      click(e);
    } else {
      console.log(e);
      openEvent(e);
    }
  };

  const onChangeEvent = (e: any) => {
    const newEvents = [...events];
    for (let le of newEvents) {
      if (le.id === e.event.id) {
        le.start = e.start;
        le.end = e.end;
      }
    }
    setEvents(newEvents);
    saveEvent(false, e.event);
  };

  const saveFromModal = () => {
    saveEvent(false, selectedEvent);
  };

  const removeEvent = () => {
    const newEvents = [];
    for (let le of events) {
      if (le.id != selectedEvent.id) {
        newEvents.push(le);
      }
    }
    setEvents(newEvents);
    saveEvent(true, selectedEvent);
    setSelectedEvent(null);
  };

  const calHeight = calendarHeight ? calendarHeight : window.innerHeight - 240;
  let scrollToTime = new Date();
  scrollToTime.setHours(7);
  scrollToTime.setMinutes(0);
  scrollToTime.setSeconds(0);

  // const handleSelectedDay = (d: Date) => {
  //   const newEvent = selectedEvent;
  //   newEvent.start = new Date(
  //     d.getFullYear(),
  //     d.getMonth(),
  //     d.getDate(),
  //     selectedEvent.start.getHours(),
  //     selectedEvent.start.getMinutes(),
  //     0
  //   );
  //   newEvent.end = new Date(
  //     d.getFullYear(),
  //     d.getMonth(),
  //     d.getDate(),
  //     selectedEvent.end.getHours(),
  //     selectedEvent.end.getMinutes(),
  //     0
  //   );
  //   setSelectedEvent(newEvent);
  //
  //   // Update the list of events for calendar
  //   const newEvents = [...events];
  //   for (let le of newEvents) {
  //     if (le.id === newEvent.id) {
  //       le.start = newEvent.start;
  //       le.end = newEvent.end;
  //     }
  //   }
  //   setEvents(newEvents);
  // };

  const setTime = (e: any, t: string) => {
    const hourMinute = e.currentTarget.value.split(":");
    const hour = parseInt(hourMinute[0]);
    const minute = parseInt(hourMinute[1]);
    const newEvent = selectedEvent;
    if (t === "from") {
      setHourFrom(hour);
      setMinuteFrom(minute);
      newEvent.start.setHours(hour, minute);
    } else if (t === "to") {
      setHourTo(hour);
      setMinuteTo(minute);
      newEvent.end.setHours(hour, minute);
    }

    setSelectedEvent(newEvent);
    // Update the list of events for calendar
    const newEvents = [...events];
    for (let le of newEvents) {
      if (le.id === newEvent.id) {
        le.start = newEvent.start;
        le.end = newEvent.end;
      }
    }
    setEvents(newEvents);
  };

  const makeRange = (from: boolean) => {
    const range: Array<string> = [];
    let hour: number;
    let minute: number;
    let hourEnd: number;
    let minuteEnd: number;
    if (from) {
      hour = 0;
      minute = 0;
      hourEnd = hourTo;
      minuteEnd = minuteTo;
    } else {
      hour = hourFrom;
      minute = minuteFrom;
      hourEnd = 24;
      minuteEnd = 0;
    }
    const minutesEnd = hourEnd * 60 + minuteEnd;
    while (hour * 60 + minute < minutesEnd) {
      range.push(twoDigits(hour) + ":" + twoDigits(minute));
      if (minute === 45) {
        minute = 0;
        hour++;
      } else {
        minute += 15;
      }
    }
    return range;
  };

  const changeSelectedEvent = (e) => {
    const newSelectedEvent = { ...selectedEvent };
    newSelectedEvent.title = e.target.value;
    setSelectedEvent(newSelectedEvent);
    const newEvents = [...events];
    for (const e of newEvents) {
      if (e.id === newSelectedEvent.id) {
        e.title = newSelectedEvent.title;
      }
    }
    setEvents(newEvents);
  };

  const handleChangedDate = (date: Date, view: string, action: string) => {
    if (view !== "week") {
      return;
    }
    let days = action === "NEXT" ? 7 : -7;
    const newEvents = [...events];
    for (const event of newEvents) {
      if (event.title === "R") {
        event.start = addDays(event.start, days);
        event.end = addDays(event.end, days);
      }
    }
    setEvents(newEvents);
  };

  const showAvailabilityHeader = () => {
    if (selectedEvent.title === "A") {
      return lang === "sv" ? "Tillgänglighet för: " : "Availability for:";
    } else if (selectedEvent.title === "R") {
      return lang === "sv" ? "Varje vecka: " : "Every week:";
    } else if (selectedEvent.title === "B") {
      return lang === "sv" ? "Blockerad tid: " : "Blocked time:";
    } else if (selectedEvent.title === "M") {
      return lang === "sv" ? "Möte: " : "Meeting:";
    } else if (selectedEvent.title === "S") {
      return lang === "sv"
        ? "Upptagen (enligt din kalender): "
        : "Busy (according to your calendar):";
    }
  };

  const renderPopupModal = () => {
    if (calType === "available") {
      return (
        <Modal isOpen={selectedEvent != null} onClose={() => saveFromModal()}>
          <ModalHeader onClose={() => saveFromModal()}>
            <>
              {lang === "sv" ? "Tillgänglighet för: " : "Availability for: "}
              {selectedEvent.title === "R" ? (
                <>
                  {daysLong[selectedEvent.start.getDay() - 1]}
                  {lang === "sv" ? "ar" : "s"}
                </>
              ) : (
                <>{zDate(selectedEvent.start, lang, true)}</>
              )}
            </>
          </ModalHeader>
          <ModalBody>
            <div className="flex items-center">
              <AccountIcon className="icon" alt="date" />
              <div className="ml-2 text-xl font-medium w-full">
                <select
                  className="z-select z-700 w-full"
                  value={selectedEvent.title}
                  onChange={(e) => changeSelectedEvent(e)}
                >
                  <option value="A">
                    {lang === "sv" ? "Tillgänglig tid" : "Available time"}
                  </option>
                  <option value="R">
                    {lang === "sv"
                      ? "Tillgänglig veckovis"
                      : "Available weekly"}
                  </option>
                  <option value="B">
                    {lang === "sv" ? "Blockera tid" : "Block time"}
                  </option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between mt-4">
              <Clock className="icon" />
              <select
                value={twoDigits(hourFrom) + ":" + twoDigits(minuteFrom)}
                className="ml-2 z-select z-700"
                onChange={(e) => setTime(e, "from")}
              >
                {makeRange(true).map((hour) => {
                  return (
                    <option key={"hour_from_" + hour} value={hour}>
                      {hour}
                    </option>
                  );
                })}
              </select>
              <div className="mx-2">&ndash;</div>
              <select
                value={twoDigits(hourTo) + ":" + twoDigits(minuteTo)}
                className="z-select z-700"
                onChange={(e) => setTime(e, "to")}
              >
                {makeRange(false).map((hour) => {
                  return (
                    <option key={"hour_to_" + hour} value={hour}>
                      {hour}
                    </option>
                  );
                })}
              </select>
            </div>
          </ModalBody>

          <ModalFooter>
            <div className="w-full flex justify-between">
              <CustomButton
                buttonType="outlineWhiteBase"
                className="w-full"
                onClick={() => removeEvent()}
              >
                {t("delete")}
              </CustomButton>
              <CustomButton
                buttonType="blueBase"
                className="ml-4 w-full"
                onClick={() => saveFromModal()}
              >
                {t("save")}
              </CustomButton>
            </div>
          </ModalFooter>
        </Modal>
      );
    } else {
      return (
        <Modal
          isOpen={selectedEvent != null}
          onClose={() => setSelectedEvent(null)}
        >
          <ModalHeader onClose={() => setSelectedEvent(null)}>
            <>
              {showAvailabilityHeader()}&nbsp;
              {selectedEvent.title === "R" ? (
                <>
                  {daysLong[selectedEvent.start.getDay() - 1]}
                  {lang === "sv" ? "ar" : "s"}
                </>
              ) : (
                <>{zDate(selectedEvent.start, lang, true)}</>
              )}
            </>
          </ModalHeader>
          <ModalBody>
            <div className="flex items-center">
              <Clock className="icon" />
              <div className="ml-4">
                {selectedEvent.start.getDate() ===
                selectedEvent.end.getDate() ? (
                  <>
                    {twoDigits(hourFrom)}:{twoDigits(minuteFrom)}&ndash;
                    {twoDigits(hourTo)}:{twoDigits(minuteTo)}
                  </>
                ) : (
                  <>
                    {dateFormatDate(selectedEvent.start, true, lang)}&ndash;
                    {dateFormatDate(selectedEvent.end, true, lang)}
                  </>
                )}
              </div>
            </div>
          </ModalBody>
        </Modal>
      );
    }
  };

  return (
    <>
      {selectedEvent != null && renderPopupModal()}

      <div className="bg-neutral-900 text-white font-hoves">
        <DnDCalendar
          components={settings.components}
          views={settings.views}
          defaultView={settings.defaultView}
          localizer={localizer}
          messages={messages}
          scrollToTime={scrollToTime}
          events={events}
          startAccessor="start"
          endAccessor="end"
          logPressThreshold={1}
          style={{ height: calHeight }}
          draggableAccessor={() => save}
          resizableAccessor={() => save}
          onSelectSlot={(e) => addEvent(e)}
          onEventDrop={(e) => onChangeEvent(e)}
          onEventResize={(e) => onChangeEvent(e)}
          onSelectEvent={(e: CalEventInterface) => editEvent(e)}
          onNavigate={(date: Date, view: string, action: string) =>
            handleChangedDate(date, view, action)
          }
          eventPropGetter={eventStyleGetter}
          selectable={settings.selectable}
          popup={true}
          resizable={settings.resizeable}
          culture={lang}
          step={30}
          showMultiDayTimes={true}
          // max={new Date(1972, 0, 1, 23, 45, 0)}
        />
      </div>
    </>
  );
};

export default CalendarBig;
